import * as z from "zod"
import student_attributes_options from "@/locales/en/student_attributes_options.json"

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never

type UnionToTuple<T> =
  UnionToIntersection<T extends any ? () => T : never> extends () => infer W
    ? [...UnionToTuple<Exclude<T, W>>, W]
    : []

function attributeEnum<TKey extends keyof typeof student_attributes_options>(
  key: TKey,
): UnionToTuple<keyof (typeof student_attributes_options)[TKey]> {
  const options = student_attributes_options[key]

  if (typeof options === "string") {
    throw new Error(`Expected object for ${key}`)
  }

  return Object.keys(options) as UnionToTuple<
    keyof (typeof student_attributes_options)[TKey]
  >
}

const gradeOptions = attributeEnum("grade_options")
export type GradeOptions = (typeof gradeOptions)[number]

const naplanOptions = attributeEnum("naplan_options")
export type NaplanOptions = (typeof naplanOptions)[number]

const parentEducationOptions = attributeEnum("parent_education_options")
export type ParentEducationOptions = (typeof parentEducationOptions)[number]

const parentOccupationOptions = attributeEnum("parent_occupation_options")
export type ParentOccupationOptions = (typeof parentOccupationOptions)[number]

const visualImpairmentOptions = attributeEnum("visual_impairment_options")
export type VisualImpairmentOptions = (typeof visualImpairmentOptions)[number]

const extentOptions = attributeEnum("extent_options")
export type ExtentOptions = (typeof extentOptions)[number]

const learningPreferenceExtentOptions = attributeEnum(
  "learning_preference_extent_options",
)
export type LearningPreferenceExtentOptions =
  (typeof learningPreferenceExtentOptions)[number]

const nonEmptySchema = z.string().trim().min(1)
const percentileSchema = z.nullable(z.number().min(0).max(100))

const adhdOptions = attributeEnum("adhd_options")
const autismOptions = attributeEnum("autism_options")
const depressionOptions = attributeEnum("depression_options")

function optionBooleansSchema<Option extends string>(options: Option[]) {
  return z.object(
    Object.fromEntries(options.map((option) => [option, z.boolean()])) as {
      [K in Option]: z.ZodBoolean
    },
  )
}

function rangeSchema<ValueSchema extends z.core.SomeType>(
  valueSchema: ValueSchema,
) {
  return z.object({
    from: valueSchema,
    to: valueSchema,
  })
}

export const specialityFormSchema = z.object({
  name: nonEmptySchema,
  description: z.string(),
  teach_attributes_exclusively: z.boolean(),

  religion_notes: z.string().optional(),
  speciality_religions_attributes: z
    .array(z.object({ religion_id: z.number() }))
    .min(1)
    .optional(),

  gender_notes: z.string().optional(),
  speciality_genders_attributes: z
    .array(z.object({ gender_id: z.number() }))
    .min(1)
    .optional(),

  primary_language_notes: z.string().optional(),
  speciality_primary_languages_attributes: z
    .array(z.object({ language_id: z.number() }))
    .min(1)
    .optional(),

  secondary_language_notes: z.string().optional(),
  speciality_secondary_languages_attributes: z
    .array(z.object({ language_id: z.number() }))
    .min(1)
    .optional(),

  overall_qualitative_assessment_attributes: z
    .object({
      overall_qualitative: rangeSchema(z.enum(gradeOptions)),
    })
    .optional(),

  subject_grade_range_attributes: z
    .object({
      grade: rangeSchema(z.enum(gradeOptions)),
    })
    .optional(),

  naplan_score_attributes: z
    .object({
      naplan_score_numeracy: rangeSchema(z.enum(naplanOptions)),
      naplan_score_reading: rangeSchema(z.enum(naplanOptions)),
      naplan_score_writing: rangeSchema(z.enum(naplanOptions)),
      naplan_score_grammar: rangeSchema(z.enum(naplanOptions)),
      naplan_score_spelling: rangeSchema(z.enum(naplanOptions)),
    })
    .optional(),

  wais_iv_attributes: z
    .object({
      wais_iv_full_scale_iq: rangeSchema(z.nullable(z.number())),
      wais_iv_general_ability: rangeSchema(z.nullable(z.number())),
      wais_iv_verbal_comprehension: rangeSchema(z.nullable(z.number())),
      wais_iv_perceptual_reasoning: rangeSchema(z.nullable(z.number())),
      wais_iv_working_memory: rangeSchema(z.nullable(z.number())),
      wais_iv_proc_speed: rangeSchema(z.nullable(z.number())),
    })
    .optional(),

  wisc_v_attributes: z
    .object({
      wisc_v_year: z.nullable(z.string()),
      wisc_v_vci: rangeSchema(z.nullable(z.number())),
      wisc_v_vsi: rangeSchema(z.nullable(z.number())),
      wisc_v_fri: rangeSchema(z.nullable(z.number())),
      wisc_v_wmi: rangeSchema(z.nullable(z.number())),
      wisc_v_psi: rangeSchema(z.nullable(z.number())),
      wisc_v_fsiq: rangeSchema(z.nullable(z.number())),
    })
    .optional(),

  wiat_iv_attributes: z
    .object({
      wiat_iv_age: z.nullable(z.string()),
      wiat_iv_total_achievement_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_word_reading: rangeSchema(z.nullable(z.number())),
      wiat_iv_reading_comprehension: rangeSchema(z.nullable(z.number())),
      wiat_iv_spelling: rangeSchema(z.nullable(z.number())),
      wiat_iv_sentence_composition: rangeSchema(z.nullable(z.number())),
      wiat_iv_math_problem_solving: rangeSchema(z.nullable(z.number())),
      wiat_iv_numerical_operations: rangeSchema(z.nullable(z.number())),
      wiat_iv_reading_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_written_expression_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_mathematics_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_basic_reading_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_decoding_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_reading_fluency_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_math_fluency_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_writing_fluency_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_oral_language_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_phonological_proc_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_ortho_proc_composite: rangeSchema(z.nullable(z.number())),
      wiat_iv_ortho_proc_extended_composite: rangeSchema(
        z.nullable(z.number()),
      ),
      wiat_iv_dyslexia_index_composite: rangeSchema(z.nullable(z.number())),
    })
    .optional(),

  aeas_attributes: z
    .object({
      aeas_score_reading: rangeSchema(percentileSchema),
      aeas_score_spelling: rangeSchema(percentileSchema),
      aeas_score_written_essay: rangeSchema(percentileSchema),
      aeas_score_listening: rangeSchema(percentileSchema),
      aeas_score_speaking: rangeSchema(percentileSchema),
      aeas_score_overall: rangeSchema(percentileSchema),
    })
    .optional(),

  parent_socio_economic_attributes: z
    .object({
      socio_economic_parent_education_requires_both: z
        .union([
          z.literal("true"),
          z.literal("false"),
          z.boolean(),
          z.undefined(),
        ])
        .transform((val) => {
          if (val === undefined) return false
          if (typeof val === "boolean") return val
          return val === "true"
        }),
      socio_economic_parent_education: rangeSchema(
        z.enum(parentEducationOptions),
      ),
      socio_economic_parent_occupation_requires_both: z
        .union([
          z.literal("true"),
          z.literal("false"),
          z.boolean(),
          z.undefined(),
        ])
        .transform((val) => {
          if (val === undefined) return false
          if (typeof val === "boolean") return val
          return val === "true"
        }),
      socio_economic_parent_occupation: rangeSchema(
        z.enum(parentOccupationOptions),
      ),
    })
    .optional(),

  eyesight_attributes: z
    .object({
      eyesight: rangeSchema(z.enum(visualImpairmentOptions)),
    })
    .optional(),

  // Medical conditions
  adhd_attributes: optionBooleansSchema(adhdOptions).optional(),
  autism_attributes: optionBooleansSchema(autismOptions).optional(),
  depression_attributes: optionBooleansSchema(depressionOptions).optional(),

  student_socio_economic_attributes: z
    .object({
      socio_economic_aboriginal_torres_strait_islander: z.boolean(),
      socio_economic_other_comment: z.string().optional(),
      socio_economic_icsea_percentile: rangeSchema(percentileSchema),
      socio_economic_icsea_comment: z.string().optional(),
    })
    .optional(),

  learning_style_description: z.string().optional(),

  learning_style_vark_attributes: z
    .object({
      learning_style_visual: rangeSchema(z.enum(extentOptions)),
      learning_style_auditory: rangeSchema(z.enum(extentOptions)),
      learning_style_kinesthetic: rangeSchema(z.enum(extentOptions)),
      learning_style_read_write: rangeSchema(z.enum(extentOptions)),
    })
    .optional(),
  learning_style_gardner_attributes: z
    .object({
      learning_style_linguistic: rangeSchema(z.enum(extentOptions)),
      learning_style_logical: rangeSchema(z.enum(extentOptions)),
      learning_style_musical: rangeSchema(z.enum(extentOptions)),
      learning_style_spatial: rangeSchema(z.enum(extentOptions)),
      learning_style_body_kinesthetic: rangeSchema(z.enum(extentOptions)),
      learning_style_interpersonal: rangeSchema(z.enum(extentOptions)),
      learning_style_intrapersonal: rangeSchema(z.enum(extentOptions)),
      learning_style_naturalistic: rangeSchema(z.enum(extentOptions)),
    })
    .optional(),

  learning_style_kolb_attributes: z
    .object({
      learning_style_concrete_exp: rangeSchema(z.enum(extentOptions)),
      learning_style_reflective_obs: rangeSchema(z.enum(extentOptions)),
      learning_style_abstract_conc: rangeSchema(z.enum(extentOptions)),
      learning_style_active_exp: rangeSchema(z.enum(extentOptions)),
    })
    .optional(),

  learning_preference_attributes: z
    .object({
      learning_preference_description: z.string(),
      learning_preference_individual: z.enum(learningPreferenceExtentOptions),
      learning_preference_group: z.enum(learningPreferenceExtentOptions),
      learning_preference_like_students: z.enum(
        learningPreferenceExtentOptions,
      ),
      learning_preference_diverse_students: z.enum(
        learningPreferenceExtentOptions,
      ),
      learning_preference_teacher_centered: rangeSchema(z.number()),
      learning_preference_structured: rangeSchema(z.number()),
      learning_preference_technology_enhanced: rangeSchema(z.number()),
      learning_preference_by_doing: rangeSchema(z.number()),
    })
    .optional(),
})

export type SpecialityFormData = z.input<typeof specialityFormSchema>
